import { useId, useState, type FormEvent } from 'react';
import Button from '../ui/Button';
import { PhotoPicker } from './CrmPhotos';
import { uploadAll } from './crmUpload';
import salesService from '../../services/sales';
import { useAuth } from '../../context/AuthContext';
import { errorMessage } from '../../utils/apiResponse';
import { getTodayInTz } from '../../utils/timezone';
import { addDays } from '../../utils/payPeriod';
import { ISSUE_CATEGORIES } from '../../utils/crm';
import type { FollowUp, FollowUpKind, IssueCategory, NewFollowUp, SalesPerson } from '../../types/sales';

/** A choice of what, within the panel's subject, it is about: one line of an order, or one order on a load. */
export interface CrmChoice { value: number; label: string }

/**
 * Makes a follow-up or raises an issue: what, by which day (tomorrow unless
 * changed), and who does it (yourself unless given to someone with a Sales
 * or Nursery seat, who is then notified). An issue has a category, and every
 * Sales Admin hears of it. Made from a voicemail, a call, a customer, an
 * order or a load, it carries that with it; the server fills the customer
 * and number from a voicemail or call, and the customer from an order.
 */
export default function FollowUpForm({
  people, tz, from = {}, defaultTitle = '', defaultKind = 'task', lineChoices, orderChoices, onSaved, onCancel,
}: {
  people: SalesPerson[];
  tz: string;
  /** What it is made from or for: a customer, an order, a load, a voicemail, a call. */
  from?: Pick<NewFollowUp, 'customer_id' | 'order_id' | 'load_id' | 'voicemail_id' | 'call_id' | 'phone'>;
  defaultTitle?: string;
  defaultKind?: FollowUpKind;
  /** On an order: its lines, to say which one it is about. */
  lineChoices?: CrmChoice[];
  /** On a load: its orders, to say which one it is about. */
  orderChoices?: CrmChoice[];
  onSaved: (fu: FollowUp) => void;
  onCancel: () => void;
}) {
  const { user } = useAuth();
  const id = useId();
  const [kind, setKind] = useState<FollowUpKind>(defaultKind);
  const [category, setCategory] = useState<IssueCategory | ''>('');
  const [title, setTitle] = useState(defaultTitle);
  const [dueOn, setDueOn] = useState(() => addDays(getTodayInTz(tz), kind === 'issue' ? 0 : 1));
  const [assignee, setAssignee] = useState<number | ''>(user?.id ?? '');
  const [details, setDetails] = useState('');
  const [line, setLine] = useState<number | ''>('');
  const [order, setOrder] = useState<number | ''>('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const meListed = user && people.some((p) => p.id === user.id);
  const isIssue = kind === 'issue';
  const ready = !!title.trim() && !!dueOn && (!isIssue || !!category);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const res = await salesService.createFollowUp({
        ...from,
        ...(line !== '' ? { order_line_id: line } : {}),
        // On a load, naming one of its orders keeps the load too.
        ...(order !== '' ? { order_id: order } : {}),
        kind,
        ...(isIssue ? { category: category as IssueCategory } : {}),
        title: title.trim(),
        due_on: dueOn,
        details: details.trim() || null,
        assigned_to_user_id: assignee === '' ? null : assignee,
      });
      if (!res.success || !res.data) throw res;
      const { saved, failed } = await uploadAll('follow-ups', res.data.id, photos);
      if (failed) setError(`Saved, but ${failed === 1 ? 'a photo' : `${failed} photos`} could not be sent.`);
      onSaved({ ...res.data, files: [...(res.data.files ?? []), ...saved] });
    } catch (err) {
      setError(errorMessage(err, isIssue ? 'Could not raise the issue' : 'Could not save the follow-up'));
    } finally {
      setBusy(false);
    }
  };

  const field = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0';
  const lbl = 'block text-xs font-medium text-neutral-600 mb-1';

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 min-w-0">
      <fieldset className="flex flex-wrap gap-4">
        <legend className="sr-only">Follow-up or issue</legend>
        {(['task', 'issue'] as const).map((k) => (
          <label key={k} className="flex items-center gap-2 text-sm text-neutral-800">
            <input type="radio" name={`${id}-kind`} checked={kind === k} onChange={() => setKind(k)} />
            {k === 'task' ? 'Follow-up' : 'Issue (something went wrong)'}
          </label>
        ))}
      </fieldset>
      {isIssue && (
        <div className="min-w-0">
          <label htmlFor={`${id}-category`} className={lbl}>What went wrong</label>
          <select id={`${id}-category`} value={category} onChange={(e) => setCategory(e.target.value as IssueCategory | '')} required
            className={`${field} sm:w-64`}>
            <option value="">Choose…</option>
            {ISSUE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      )}
      {lineChoices && lineChoices.length > 0 && (
        <div className="min-w-0">
          <label htmlFor={`${id}-line`} className={lbl}>Which line (optional)</label>
          <select id={`${id}-line`} value={line} onChange={(e) => setLine(e.target.value ? Number(e.target.value) : '')} className={`${field} sm:w-80`}>
            <option value="">The whole order</option>
            {lineChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      )}
      {orderChoices && orderChoices.length > 0 && (
        <div className="min-w-0">
          <label htmlFor={`${id}-order`} className={lbl}>Which order (optional)</label>
          <select id={`${id}-order`} value={order} onChange={(e) => setOrder(e.target.value ? Number(e.target.value) : '')} className={`${field} sm:w-80`}>
            <option value="">The whole load</option>
            {orderChoices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      )}
      <div className="min-w-0">
        <label htmlFor={`${id}-title`} className={lbl}>{isIssue ? 'What happened' : 'What to do'}</label>
        <input id={`${id}-title`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} required
          placeholder={isIssue ? 'Three pots broken in the stillage' : 'Ring back about the spring order'}
          className={field} />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 min-w-0">
        <div className="min-w-0">
          <label htmlFor={`${id}-due`} className={lbl}>{isIssue ? 'Sort out by' : 'By'}</label>
          <input id={`${id}-due`} type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} required
            className={`${field} sm:w-44`} />
        </div>
        <div className="min-w-0 flex-1">
          <label htmlFor={`${id}-who`} className={lbl}>Who does it</label>
          <select id={`${id}-who`} value={assignee} onChange={(e) => setAssignee(e.target.value ? Number(e.target.value) : '')}
            className={`${field} sm:w-64`}>
            {!meListed && <option value={user?.id ?? ''}>Me</option>}
            {people.map((p) => <option key={p.id} value={p.id}>{p.id === user?.id ? `${p.name} (me)` : p.name}</option>)}
          </select>
        </div>
      </div>
      <div className="min-w-0">
        <label htmlFor={`${id}-details`} className={lbl}>Details (optional)</label>
        <textarea id={`${id}-details`} value={details} onChange={(e) => setDetails(e.target.value)} rows={2} maxLength={4000}
          className={field} />
      </div>
      <PhotoPicker files={photos} onChange={setPhotos} />
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" isLoading={busy} disabled={!ready}>{isIssue ? 'Raise issue' : 'Save follow-up'}</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
